// Markdown parsing for product descriptions.
//
// Descriptions come from the AI-generate service and from operators
// hand-writing the same field. The AI-generate system prompt
// (`buildSystemPrompt` in
// _src/services/aldelo-storefront/internal/marketplace/service/ai_generate_service.go)
// now enumerates exactly this renderer's supported markdown subset, so the
// prompt and this grammar must change IN LOCKSTEP: a change to this grammar
// requires the same-commit prompt update, pinned on the Go side by
// TestBuildSystemPrompt_NamesSupportedMarkdownSubset
// (ai_generate_service_test.go). This module turns that source into a small
// block/inline tree; the renderers turn the tree into elements.
//
// WHY A PARSER AND NOT A LIBRARY. The customer embed loads inside third-party
// Framer sites and carries zero runtime dependencies beyond React, so bundle
// weight is a real constraint. More importantly, this module NEVER produces
// HTML — it produces data that the renderers turn into React elements, so
// React's own escaping makes markup injection structurally impossible rather
// than filtered. The one place a parsed string reaches an HTML attribute is a
// link `href`, which is why the scheme allowlist below is a security boundary
// and not a nicety.
//
// The grammar is a deliberate SUBSET of CommonMark covering what the prompt
// asks for: ATX headings, unordered/ordered lists, blank-line paragraphs, and
// inline strong/em/code/link. Anything outside the subset (tables, block
// quotes, fenced code, nested lists, raw HTML) survives as literal text —
// degraded, never dropped and never executed.

export type MdInline =
  | { kind: "text"; value: string }
  | { kind: "strong"; children: MdInline[] }
  | { kind: "em"; children: MdInline[] }
  | { kind: "code"; value: string }
  | { kind: "link"; href: string; children: MdInline[] };

export type MdBlock =
  | { kind: "heading"; level: 1 | 2 | 3; children: MdInline[] }
  | { kind: "paragraph"; children: MdInline[] }
  | { kind: "list"; ordered: boolean; items: MdInline[][] };

/**
 * Schemes a link may carry. Everything else — `javascript:`, `data:`, `ftp:`,
 * and scheme-less/relative paths — degrades to plain text so the words survive
 * but nothing navigable is produced. Relative paths are excluded deliberately:
 * the embed renders on customer-owned origins, so a relative href would
 * resolve against their site, not ours.
 */
const ALLOWED_LINK_SCHEMES = new Set(["http:", "https:", "mailto:"]);

function isAllowedHref(raw: string): boolean {
  // Trim first: leading whitespace is stripped by the HTML parser when it
  // resolves a URL, so "  javascript:…" is live in a browser even though it
  // fails a naive startsWith check.
  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(raw.trim());
  return scheme !== null && ALLOWED_LINK_SCHEMES.has(scheme[1].toLowerCase() + ":");
}

const isSpace = (ch: string | undefined): boolean => ch === undefined || /\s/.test(ch);

/**
 * Finds a closing emphasis delimiter at or after `from`.
 *
 * A delimiter only closes when the character before it is non-whitespace —
 * the flanking rule that stops "2 * 3 = 6 and a lone ** here" from being read
 * as emphasis spanning the whole sentence. Without it, arithmetic and stray
 * markers in operator-written prose silently swallow their surroundings.
 */
function findCloser(src: string, from: number, marker: string): number {
  let at = from;
  for (;;) {
    at = src.indexOf(marker, at);
    if (at < 0) return -1;
    if (!isSpace(src[at - 1])) return at;
    at += marker.length;
  }
}

/**
 * Finds the index of the `)` closing a link destination that starts at
 * `openParen + 1`, counting nesting so a destination containing parentheses
 * — `javascript:alert(1)`, or a Wikipedia-style URL — is captured whole.
 * Returns -1 when unbalanced.
 *
 * This matters for correctness of the REJECT path as much as the accept path:
 * stopping at the first `)` would capture `javascript:alert(1`, reject it (so
 * far so good), and then leave a stray `)` in the visible text.
 */
function findDestEnd(src: string, openParen: number): number {
  let depth = 1;
  for (let at = openParen + 1; at < src.length; at++) {
    if (src[at] === "(") depth++;
    else if (src[at] === ")" && --depth === 0) return at;
  }
  return -1;
}

function parseInline(src: string): MdInline[] {
  const out: MdInline[] = [];
  let buf = "";
  const flush = () => {
    if (buf !== "") {
      out.push({ kind: "text", value: buf });
      buf = "";
    }
  };

  let i = 0;
  while (i < src.length) {
    const ch = src[i];

    // `code` — highest precedence, contents stay literal.
    if (ch === "`") {
      const end = src.indexOf("`", i + 1);
      if (end > i + 1) {
        flush();
        out.push({ kind: "code", value: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    // **strong** / __strong__ / *em* / _em_
    if (ch === "*" || ch === "_") {
      const marker = src[i + 1] === ch ? ch + ch : ch;
      const contentStart = i + marker.length;
      // An opener must be followed by non-whitespace (flanking rule).
      if (!isSpace(src[contentStart])) {
        const end = findCloser(src, contentStart, marker);
        if (end > contentStart) {
          flush();
          const children = parseInline(src.slice(contentStart, end));
          out.push(
            marker.length === 2 ? { kind: "strong", children } : { kind: "em", children },
          );
          i = end + marker.length;
          continue;
        }
      }
    }

    // [label](destination)
    if (ch === "[") {
      const labelEnd = src.indexOf("]", i + 1);
      if (labelEnd > i && src[labelEnd + 1] === "(") {
        const destEnd = findDestEnd(src, labelEnd + 1);
        if (destEnd > 0) {
          const dest = src.slice(labelEnd + 2, destEnd);
          const children = parseInline(src.slice(i + 1, labelEnd));
          flush();
          if (isAllowedHref(dest)) {
            out.push({ kind: "link", href: dest.trim(), children });
          } else {
            // Keep the words, drop the link. Discarding the label too would
            // silently delete operator-authored content.
            out.push(...children);
          }
          i = destEnd + 1;
          continue;
        }
      }
    }

    buf += ch;
    i++;
  }

  flush();
  return out;
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const UNORDERED_ITEM = /^[-*+]\s+(.*)$/;
const ORDERED_ITEM = /^\d+[.)]\s+(.*)$/;

/**
 * Parses markdown source into a flat list of blocks.
 *
 * Headings deeper than `###` clamp to level 3 — the renderers offset levels
 * below the surrounding page heading, and an unbounded level would produce
 * invalid heading tags.
 */
export function parseMarkdown(source: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      // Soft-wrapped lines join with a space: the model wraps prose at
      // arbitrary widths and those newlines are not paragraph breaks.
      blocks.push({ kind: "paragraph", children: parseInline(paragraph.join(" ")) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list !== null) {
      blocks.push({ kind: "list", ordered: list.ordered, items: list.items.map(parseInline) });
      list = null;
    }
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  const openItem = (ordered: boolean, text: string) => {
    flushParagraph();
    if (list !== null && list.ordered !== ordered) flushList();
    if (list === null) list = { ordered, items: [] };
    list.items.push(text.trim());
  };

  for (const rawLine of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = rawLine.trim();

    if (line === "") {
      flushAll();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading !== null) {
      flushAll();
      blocks.push({
        kind: "heading",
        level: Math.min(heading[1].length, 3) as 1 | 2 | 3,
        children: parseInline(heading[2].trim()),
      });
      continue;
    }

    const unordered = UNORDERED_ITEM.exec(line);
    if (unordered !== null) {
      openItem(false, unordered[1]);
      continue;
    }

    const ordered = ORDERED_ITEM.exec(line);
    if (ordered !== null) {
      openItem(true, ordered[1]);
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushAll();
  return blocks;
}

function flattenInline(nodes: MdInline[]): string {
  return nodes
    .map((node) =>
      node.kind === "text" || node.kind === "code" ? node.value : flattenInline(node.children),
    )
    .join("");
}

/**
 * Flattens markdown to a single line of prose — markup removed, words kept.
 *
 * Used where the description appears in a fixed-height clamp (the catalog grid
 * tile), where rendering headings and bullets would clip mid-element and break
 * the tile's price alignment. Link text survives; the URL does not.
 */
export function stripMarkdown(source: string): string {
  const parts: string[] = [];
  for (const block of parseMarkdown(source)) {
    if (block.kind === "list") parts.push(...block.items.map(flattenInline));
    else parts.push(flattenInline(block.children));
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}
